import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  IonModal,
  IonButton,
  IonCheckbox,
  IonSpinner,
  IonInput,
} from '@ionic/react';
import { attachOutline, searchOutline, closeOutline } from 'ionicons/icons';
import filterIcon from '../../../icons/filter.svg';
import { useAnnotations } from '../hooks/use-annotations.hook';
import { useUserContext } from '../../user/contexts/user.context';
import { AnnotationFilters } from './annotation-filters.component';
import { AnnotationFilterView } from '../../plan/types/annotation.types';
import { AnnotationStatus } from '../../plan/types/annotation.types';
import { AnnotationModel } from '../../plan/models/annotation.model';
import './annotations-list.component.scss';
import { AnnotationForm } from '../../plan/components/annotation-form.component';
import { useAnnotationIdContext } from '../../plan/contexts/annotation-id.context';

// Type étendu pour inclure le score de pertinence
interface AnnotationWithRelevanceScore extends AnnotationModel {
  relevanceScore: number;
}

const EMPTY_FILTER: AnnotationFilterView = {
  siteIds: null,
  annotationTypes: null,
  annotationStatus: null,
  hasAttachment: null,
  annotationAuthor: null,
  annotationPortfolios: null
};

const ANNOTATION_STATUSES: AnnotationStatus[] = ['NEW', 'TODO', 'IN_PROGRESS', 'PENDING', 'DONE', 'ARCHIVED'];

// Configuration des poids de pertinence pour la recherche
const RELEVANCE_WEIGHTS = {
  title: 10,        // Titre
  content: 5,       // Contenu
  siteCode: 9,      // Code du site
  location: 3,       // Location
  author: 3       // Author
} as const;

export const AnnotationsList: React.FC<{ siteId?: string }> = ({ siteId }) => {
  const { t } = useTranslation();
  const { currentOrganization, hasUser } = useUserContext();
  const { setAnnotationIdFromList } = useAnnotationIdContext();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<AnnotationFilterView>(EMPTY_FILTER);
  const [sortBy, setSortBy] = useState<string>('date_asc');
  const [selectedAnnotations, setSelectedAnnotations] = useState<Set<string>>(new Set());
  const [activeFiltersCount, setActiveFiltersCount] = useState<number>(4);
  const [searchText, setSearchText] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Utiliser le code organisation de manière sécurisée pour les hooks
  const organizationCode = currentOrganization?.code || "";

  const {
    annotations,
    loading,
    error,
    loadAnnotations,
    updateAnnotationsStatus,
    DEFAULT_FILTER_WITH_SITE,
    selectedAnnotation,
    handleAnnotationClick,
    closeAnnotationDetail,
    updateAnnotation,
    deleteAnnotation,
    availableAuthors,
    availablePortfolios,
    availableSites,
  } = useAnnotations(organizationCode, siteId);

  useEffect(() => {
    // Ne charger les annotations que si l'organisation est disponible
    if (hasUser && currentOrganization) {
      loadAnnotations(DEFAULT_FILTER_WITH_SITE);
    }
  }, [DEFAULT_FILTER_WITH_SITE, loadAnnotations, hasUser, currentOrganization]);

  const handleFilterChange = (newFilter: AnnotationFilterView) => {
    setCurrentFilter(newFilter);
    
    // Si on est sur une page de site spécifique, préserver l'ID du site dans le filtre
    const finalFilter = siteId ? {
      ...newFilter,
      siteIds: [siteId]
    } : newFilter;
    
    loadAnnotations(finalFilter);
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
  };

  const handleCheckboxChange = (annotationId: string) => {
    setSelectedAnnotations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(annotationId)) {
        newSet.delete(annotationId);
      } else {
        newSet.add(annotationId);
      }
      return newSet;
    });
  };

  const filteredAndSortedAnnotations = useMemo(() => {
    let filtered = annotations;
    
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      
      // Calculer un score de pertinence pour chaque annotation
      const annotationsWithScore: AnnotationWithRelevanceScore[] = annotations.map(annotation => {
        let score = 0;
        
        // Titre : poids le plus élevé
        if (annotation.title?.toLowerCase().includes(searchLower)) {
          score += RELEVANCE_WEIGHTS.title;
        }
        
        // Contenu : poids moyen
        if (annotation.content?.toLowerCase().includes(searchLower)) {
          score += RELEVANCE_WEIGHTS.content;
        }
        
        // Code du site : poids faible
        if (annotation.siteCode?.toLowerCase().includes(searchLower)) {
          score += RELEVANCE_WEIGHTS.siteCode;
        }
        
        // Location : poids faible
        if (annotation.location?.toLowerCase().includes(searchLower)) {
          score += RELEVANCE_WEIGHTS.location;
        }
        
        // Location : poids faible
        if (annotation.addedBy?.toLowerCase().includes(searchLower)) {
          score += RELEVANCE_WEIGHTS.location;
        }

        // Créer une nouvelle instance avec le score de pertinence
        const annotationWithScore = Object.create(annotation);
        annotationWithScore.relevanceScore = score;
        return annotationWithScore;
      });
      
      // Filtrer les annotations avec un score > 0 et trier par score décroissant
      filtered = annotationsWithScore
        .filter(annotation => annotation.relevanceScore > 0)
        .sort((a, b) => b.relevanceScore - a.relevanceScore);
    }

    // Si une recherche est active, garder le tri par pertinence
    // Sinon, appliquer le tri manuel (date, code site, etc.)
    if (!searchText.trim()) {
      return filtered.sort((a, b) => {
        if (sortBy === 'date_desc') {
          const dateA = a.date ? parseInt(a.date) : 0;
          const dateB = b.date ? parseInt(b.date) : 0;
          return dateB - dateA;
        } else if (sortBy === 'date_asc') {
          const dateA = a.date ? parseInt(a.date) : 0;
          const dateB = b.date ? parseInt(b.date) : 0;
          return dateA - dateB;
        } else if (sortBy === 'site_code_asc') {
          const codeA = a.siteCode || '';
          const codeB = b.siteCode || '';
          return codeA.localeCompare(codeB);
        } else if (sortBy === 'site_code_desc') {
          const codeA = a.siteCode || '';
          const codeB = b.siteCode || '';
          return codeB.localeCompare(codeA);
        }
        return 0;
      });
    }
    
    // Retourner les résultats triés par pertinence si une recherche est active
    return filtered;
  }, [annotations, sortBy, searchText]);

  const openFilters = () => {
    setIsFiltersOpen(true);
  };

  const handleAnnotationIdPass = (annotationId: string) => {
    setAnnotationIdFromList(annotationId);
  };

  const handleSearchChange = (e: CustomEvent) => {
    const value = e.detail.value!;
    setSearchText(value);
  };

  const clearSearch = () => {
    setSearchText('');
  };

  // Ne pas rendre le composant si l'organisation n'est pas encore chargée
  if (!hasUser || !currentOrganization) {
    return null;
  }

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/sites" />
            </IonButtons>
            <IonTitle>{t('annotations.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ '--padding-top': '104px' }}>
          <IonGrid style={{ height: "calc(100vh - 104px)" }}>
            <IonRow style={{ height: "100%" }} className="ion-align-items-start">
              <IonCol style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "20px" }}>
                <IonSpinner />
                <IonText>{t('annotations.loadingMessage')}</IonText>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    );
  }

  if (error) {
    return (
      <IonPage>
        <IonContent>
          <IonText color="danger">{t('annotations.errorMessage')}</IonText>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage data-testid="annotations-list">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/sites" />
          </IonButtons>
          <IonTitle>{t('annotations.title')}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '0 16px',
            height: '48px'
          }}>
            <IonCheckbox
              checked={selectedAnnotations.size === filteredAndSortedAnnotations.length}
              onIonChange={() => {
                if (selectedAnnotations.size === filteredAndSortedAnnotations.length) {
                  setSelectedAnnotations(new Set());
                } else {
                  setSelectedAnnotations(new Set(filteredAndSortedAnnotations.map(a => a.id)));
                }
              }}
              style={{
                marginRight: '8px',
                '--size': '24px',
                '--checkbox-background-checked': '#FF2953',
                '--border-color-checked': '#FF2953',
                '--border-radius': '0'
              }}
            />
            <span style={{
              fontFamily: 'Open Sans, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: '#4B4D56'
            }}>
              {t('annotations.filter.selectAll')} ({selectedAnnotations.size}/{filteredAndSortedAnnotations.length})
            </span>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--padding-bottom': '84px' }}>
        {/* Champ de recherche */}
        <div style={{
          position: 'fixed',
          top: '104px',
          left: '0',
          right: '0',
          zIndex: 1000,
          padding: '24px 16px 8px 16px',
          backgroundColor: '#F7F8FB'
        }}>

          <IonItem
            lines="none"
            style={{
              width: '100%',
              background: '#F7F8FB',
              border: '1px solid #ccc',
              borderRadius: '100px',
              paddingLeft: '8px',
              paddingRight: '8px',
              alignItems: 'center',
              height: '40px',
              display: 'flex',
            }}
          >
            <IonIcon
              icon={searchOutline}
              slot="start"
              style={{
                fontSize: '16px',
                color: !searchText ? '#888' : '#fff',
                backgroundColor: !searchText
                  ? 'transparent'
                  : isSearchFocused
                    ? '#165BE6'
                    : '#4B4D55',
                borderRadius: '50%',
                padding: '4px',
                height: '24px',
                width: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.3s',
                marginRight: '6px',
                verticalAlign: 'middle',
              }}
            />

            <IonInput
              placeholder={t('annotationsSearch.placeholder')}
              value={searchText}
              onIonInput={handleSearchChange}
              onIonFocus={() => setIsSearchFocused(true)}
              onIonBlur={() => setIsSearchFocused(false)}
              style={{
                font:'open-sans, sans-serif',
                weight: '400',
                background: 'transparent',
                flex: 1,
                fontSize: '12px',
                lineHeight: '100%',
                verticalAlign: 'middle',
                height:'100%',
                alignItems: 'center',
              }}
              clearInput={false}
              role="search"
              enterkeyhint="search"
            />

            {searchText && (
              <IonButton
                fill="clear"
                size="small"
                onClick={clearSearch}
                style={{
                  padding: '2px',
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  verticalAlign: 'middle',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                className="no-hover-bg"
              >
                <IonIcon
                  icon={closeOutline}
                  slot="icon-only"
                  style={{
                    fontSize: '14px',
                    color: '#888',
                    backgroundColor: 'transparent',
                  }}
                />
              </IonButton>
            )}
          </IonItem>
        </div>

        <div style={{
          position: 'fixed',
          top: '164px',
          right: '16px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '8px 0'
        }}>
          <IonButton
            onClick={openFilters}
            style={{
              color: '#4B4D56',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500',
              fontSize: '12px',
              lineHeight: '20px',
              fontFamily: 'open-sans, sans-serif',
              letterSpacing: '0.5px',
              textTransform: 'none',
              '--box-shadow': 'none',
              '--background': 'transparent',
              '--background-hover': 'transparent',
              '--background-activated': 'transparent',
              '--background-focused': 'transparent'
            }}
          >
            <img src={filterIcon} alt="filter" style={{ marginRight: '4px', width: '16px', height: '16px' }} />
            {activeFiltersCount > 0 ? `${t('annotations.filter.button')} (${activeFiltersCount})` : t('annotations.filter.button')}
          </IonButton>
        </div>

        <IonModal
          isOpen={isFiltersOpen}
          onDidDismiss={() => setIsFiltersOpen(false)}
          className="right-drawer"
        >
          <AnnotationFilters 
            onClose={() => setIsFiltersOpen(false)}
            onFilterChange={handleFilterChange}
            currentFilter={currentFilter}
            currentSort={sortBy}
            onSortChange={handleSortChange}
            onActiveFiltersCountChange={setActiveFiltersCount}
            hideSiteFilter={!!siteId}
            availableAuthors={availableAuthors}
            availablePortfolios={availablePortfolios}
            availableSites={availableSites}
          />
        </IonModal>

        <IonGrid>
          <IonRow style={{ height: '112px' }}>
            <IonCol size="12">
            </IonCol>
          </IonRow>

          <IonRow style={{ marginBottom: '84px' }}>
            {filteredAndSortedAnnotations.map(annotation => (
              <IonCol size="12" sizeLg="3" key={annotation.id}>
                <div 
                  onClick={() => handleAnnotationClick(annotation)}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '6px',
                    padding: '0',
                    margin: '8px',
                    boxShadow: '1px 1px 3px 0px var(--elevation-xs, rgba(0, 0, 0, 0.20))',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                >
                  <IonItem 
                    button 
                    className="annotation-item"
                    detail={true}
                    style={{
                      '--background': 'transparent',
                      '--border-radius': '6px',
                      '--padding-start': '0',
                      '--padding-end': '0',
                      '--inner-padding-end': '0',
                      margin: '0',
                      '--border-style': 'none'
                    }}
                  >
                    <IonLabel>
                      <div className="annotation-container">
                        <div className="annotation-content">
                          <div style={{
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '4px'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start'
                            }}>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <IonCheckbox
                                  checked={selectedAnnotations.has(annotation.id)}
                                  onIonChange={(e) => {
                                    e.stopPropagation();
                                    handleCheckboxChange(annotation.id);
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  style={{
                                    marginRight: '8px',
                                    '--size': '24px',
                                    '--checkbox-background-checked': '#FF2953',
                                    '--border-color-checked': '#FF2953',
                                    '--border-radius': '0'
                                  }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <p style={{
                                      fontFamily: 'Open Sans, sans-serif',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      lineHeight: '1.5',
                                      letterSpacing: '0.5px',
                                      color: '#4B4D56',
                                      margin: 0
                                    }}>{annotation.date ? (() => {
                                      const year = annotation.date.substring(0, 4);
                                      const month = annotation.date.substring(4, 6);
                                      const day = annotation.date.substring(6, 8);
                                      return `${day}/${month}/${year}`;
                                    })() : ''}</p>
                                    
                                    <p style={{
                                      fontFamily: 'Open Sans, sans-serif',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      lineHeight: '1.5',
                                      letterSpacing: '0.5px',
                                      color: '#4B4D56',
                                      margin: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}>
                                      <span style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: annotation.type === 'CREATION' ? '#389B49' : 
                                                       annotation.type === 'DELETION' ? '#E93A1C' :
                                                         annotation.type === 'MODIFICATION' ? '#E150FB' :
                                                       '#165BE6'
                                      }}></span>
                                      {t(`plan.annotationForm.form.type.${annotation.type.toLowerCase()}`)}
                                    </p>
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {annotation.fileName && (
                                      <IonIcon icon={attachOutline} style={{ marginRight: '4px' }} />
                                    )}
                                    <h2 style={{ 
                                      margin: 0,
                                      fontFamily: 'Open Sans, sans-serif',
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      lineHeight: '1.5',
                                      letterSpacing: '0.5px',
                                      verticalAlign: 'middle'
                                    }}>{annotation.title}</h2>
                                  </div>

                                  <p style={{
                                    fontFamily: 'Open Sans, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    lineHeight: '1.5',
                                    letterSpacing: '0.5px',
                                    color: '#4B4D56',
                                    margin: 0,
                                    width: '100%',
                                    flex: 1,
                                    minWidth: 0
                                  }}>{annotation.location?.toLowerCase()}</p>
                                </div>
                              </div>
                              <p 
                                title={t(`annotations.filter.statusValues.${annotation.status}`)}
                                style={{
                                  fontFamily: 'Open Sans, sans-serif',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  lineHeight: '1.5',
                                  letterSpacing: '0.5px',
                                  color: annotation.status && ['TODO', 'IN_PROGRESS', 'PENDING', 'NEW'].includes(annotation.status) ? '#CA6700' : '#389B49',
                                  margin: 0,
                                  whiteSpace: 'nowrap',
                                  minWidth: '100px',
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textAlign: 'right'
                                }}
                              >{t(`annotations.filter.statusValues.${annotation.status}`)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </IonLabel>
                  </IonItem>
                </div>
              </IonCol>
            ))}
          </IonRow>
        </IonGrid>
      </IonContent>
      <IonButton
        expand="block"
        disabled={selectedAnnotations.size === 0}
        onClick={() => {
          const select = document.createElement('ion-select');
          select.interface = 'popover';
          select.okText = t('annotations.editStatus.ok');
          select.cancelText = t('annotations.editStatus.cancel');
          select.interfaceOptions = {
            header: t('annotations.editStatus.title'),
            cssClass: 'my-custom-interface'
          };
          
          ANNOTATION_STATUSES.forEach(status => {
            const option = document.createElement('ion-select-option');
            option.value = status;
            option.textContent = t(`annotations.filter.statusValues.${status}`);
            select.appendChild(option);
          });

          select.addEventListener('ionChange', async (e: any) => {
            const status = e.detail.value;
            if (status && selectedAnnotations.size > 0) {
              await updateAnnotationsStatus(Array.from(selectedAnnotations), status);
              setSelectedAnnotations(new Set());
            }
          });

          select.addEventListener('ionDismiss', () => {
            select.remove();
          });

          document.body.appendChild(select);
          select.open();
        }}
        style={{
          color: '#FFFFFF',
          opacity: 1,
          height: '52px',
          margin: '0',
          '--padding-start': '12px',
          '--padding-end': '12px',
          '--background': selectedAnnotations.size === 0 ? '#DDDDDD' : '#FF2953',
          textTransform: 'none',
          position: 'fixed',
          bottom: 'env(safe-area-inset-bottom, 0)',
          left: 0,
          right: 0,
          width: '100%',
          padding: '8px',
          zIndex: 1000,
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          WebkitBackfaceVisibility: 'hidden',
          WebkitPerspective: '1000'
        }}
      >
        {t('annotations.filter.editStatus')}
      </IonButton>

      <IonModal isOpen={selectedAnnotation !== null}>
          {selectedAnnotation && (
            <AnnotationForm
              isPlanExported={false}
              annotationToVisualize={selectedAnnotation}
              createAnnotation={() => {}}
              updateAnnotation={updateAnnotation}
              deleteAnnotation={deleteAnnotation}
              closePopover={closeAnnotationDetail}
              annotationPositionName={selectedAnnotation.location || ''}
              showDisplayPlanIcon={true}
              roomId={selectedAnnotation.roomId}
              onAnnotationIdPass={handleAnnotationIdPass}
            />
          )}
      </IonModal>
      
    </IonPage>
  );
};
